import type * as hast from 'hast';
import { find } from 'unist-util-find';

import { code, integration, math } from '@gitbook/doc-core';

import { lookupIntegrationBlockInSet } from '../../utils';
import { parseCodeBlockLines } from './parseCodeBlockLines';
import type { HastToGastTransformMap } from './types';
import { getClassNamesAttribute, getCustomAttributes } from './utils';

export const transformBlockCodes: HastToGastTransformMap = {
    pre: async (node: hast.Element, eat, context, options) => {
        const codeNode = find<hast.Element>(
            node,
            (node: hast.ElementContent) => node.type === 'element' && node.tagName === 'code'
        );

        // Extract all other attributes passed to the code block
        // When parsing markdown, these attributes are the ones stored in the meta of the codeblock
        const codeCustomAttributes = codeNode ? getCustomAttributes(codeNode) : {};
        delete codeCustomAttributes.className;

        // Extract the syntax from the class name
        const syntaxClass = codeNode
            ? getClassNamesAttribute(codeNode.properties).find(
                  (cn) => cn.indexOf('language-') === 0 || cn.indexOf('lang-') === 0
              )
            : null;
        const syntax = syntaxClass ? syntaxClass.substring(syntaxClass.indexOf('-') + 1) : null;

        const children = await eat('inlines', node.children, { ...context, sanitizeSpaces: false });
        const lines = parseCodeBlockLines(children);

        // GitHub markdown uses 'math' as a syntax for math blocks
        if (syntax && syntax.trim().toLowerCase() === 'math') {
            const textContent = lines.map((line) => line.text).join('\n');
            return [math.createMathBlock(textContent)];
        }

        // Lookup the code-block as an integration block
        const integrationBlock = lookupIntegrationBlockInSet(
            options.integrationBlocks || {},
            (entry) => {
                return entry.block.markdown?.codeblock === syntax;
            }
        );
        if (integrationBlock) {
            const textContent = lines.map((line) => line.text).join('\n');
            const { fullWidth, ...integrationCodeCustomAttributes } = codeCustomAttributes;

            return [
                integration.createIntegrationBlock(
                    integrationBlock.integrationKey,
                    integrationBlock.block.id,
                    {
                        ...integrationCodeCustomAttributes,
                        [integrationBlock.block.markdown!.body]: textContent,
                    },
                    undefined,
                    typeof fullWidth !== 'undefined' ? fullWidth !== 'false' : undefined
                ),
            ];
        }

        return [
            code.createCodeBlock(lines, {
                ...(syntax ? { syntax } : {}),
                ...(typeof node.properties?.dataTitle === 'string'
                    ? { title: node.properties.dataTitle }
                    : {}),
                ...(typeof node.properties?.dataOverflow === 'string'
                    ? { overflow: node.properties.dataOverflow as 'scroll' | 'wrap' }
                    : {}),
                ...(typeof node.properties?.dataLineNumbers !== 'undefined'
                    ? { lineNumbers: true }
                    : {}),
                ...(typeof node.properties.dataFullWidth !== 'undefined'
                    ? { fullWidth: node.properties.dataFullWidth !== 'false' }
                    : undefined),
                ...(typeof node.properties?.dataExpandable !== 'undefined'
                    ? { expandable: node.properties.dataExpandable !== 'false' }
                    : undefined),
                ...(typeof node.properties?.dataCollapsedLineCount !== 'undefined' &&
                !Number.isNaN(node.properties.dataCollapsedLineCount) &&
                Number(node.properties.dataCollapsedLineCount) > 0
                    ? { collapsedLineCount: Number(node.properties.dataCollapsedLineCount) }
                    : {}),
            }),
        ];
    },
};
